/*
 * TunePad
 *
 * Michael S. Horn
 * Northwestern University
 * michael-horn@northwestern.edu
 *
 * This project was funded by the National Science Foundation (grant DRL-1612619).
 * Any opinions, findings and conclusions or recommendations expressed in this
 * material are those of the author(s) and do not necessarily reflect the views
 * of the National Science Foundation (NSF).
 */
import { PythonRuntime } from '../../compiler/runtime';
import { MusicTrace } from '../../audio';
import html from './canvas.module.html';
import styles from './canvas.module.css' with { type: 'css' };


/**
 * ## TimelineCanvas (code can animate and draw on this)
 * ```html
 * <timeline-canvas>
 * ```
 */
export class TimelineCanvas extends HTMLElement {

    static readonly ELEMENT = "timeline-canvas";

    static observedAttributes = [ "width", "height" ];

    /** unique id for this canvas */
    uuid : string = crypto.randomUUID();


    constructor() {
        super();
    }


    async connectedCallback() {
        this.innerHTML = html;
        const canvas = this.querySelector('canvas') as HTMLCanvasElement;
        canvas.setAttribute('data-uuid', this.uuid);
        await this.waitForWindowLoad();
        PythonRuntime.registerCanvas(this.uuid, canvas);

        // register pointer events for the canvas
        [
            'click',
            'pointerdown',
            'pointerup',
            'pointermove',
            'pointerenter',
            'pointerleave'
        ]
        .forEach(event => {
            canvas.addEventListener(event, e => {
                const rect = canvas.getBoundingClientRect();
                const sx = (canvas.width / rect.width);
                const sy = (canvas.height / rect.height);
                const x = (e as PointerEvent).offsetX * sx;
                const y = (e as PointerEvent).offsetY * sy;
                this.dispatchEvent(new CustomEvent(event, {
                    bubbles: true, composed: true, detail: { x, y }
                }));
                if (event === 'pointermove' && (e as PointerEvent).buttons > 0) {
                    this.dispatchEvent(new CustomEvent('pointerdrag', {
                        bubbles: true, composed: true, detail: { x, y }
                    }));
                }
            });
        });
    }


    disconnectedCallback() {
        PythonRuntime.unregisterCanvas(this.uuid);
    }


    attributeChangedCallback(name : string, oldValue : string, newValue : string) { }

    public setTrace(t : MusicTrace, force : boolean = false) { }

    private async waitForWindowLoad() : Promise<void> {
        return new Promise((resolve) => {
            if (document.readyState === 'complete') {
                resolve();
            } else {
                window.addEventListener('load', () => {
                    resolve();
                });
            }
        });
    }
}
